import React, { useEffect, useMemo } from 'react';
import { Autocomplete, TextField, Box } from '@mui/material';
import { useRecoilValue, useRecoilCallback } from 'recoil';
import { useNavigate, useLocation } from 'react-router-dom';
import { selectedRegionState, forecastAndStatisticData } from '../../../atoms/Forecast';
import { getRegions } from '../../../atoms/regions';

const renderRegionsInput = (params) => <TextField {...params} label="Regions" />;

const Filter = () => {
  const regions = useRecoilValue(getRegions);
  // refresh forecast data when the region changes, then set the selected region state
  const setSelectedRegion = useRecoilCallback(
    ({ set, refresh }) =>
      (region) => {
        refresh(forecastAndStatisticData);
        set(selectedRegionState, region);
      },
    [],
  );
  const navigate = useNavigate();
  const location = useLocation();

  const regionFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('region');
  }, [location.search]);

  const selectedOption = useMemo(() => {
    return regions.find((r) => r.value === regionFromUrl) || null;
  }, [regions, regionFromUrl]);

  // Single source of truth: sync URL query param → Recoil state
  useEffect(() => {
    if (regions.length === 0) return;

    if (regionFromUrl) {
      const matched = regions.find((r) => r.value === regionFromUrl);
      if (matched) {
        setSelectedRegion(matched.value);
      } else {
        // If URL has invalid region, reset to no selection
        setSelectedRegion(null);
      }
    } else {
      setSelectedRegion(null);
    }
  }, [regionFromUrl, regions]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRegionChange = (event, option) => {
    const regionCode = option?.value || null;

    // Only update URL — useEffect above will handle syncing to Recoil state
    const params = new URLSearchParams(location.search);
    if (regionCode) {
      params.set('region', regionCode);
    } else {
      params.delete('region');
    }
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  };

  return (
    <Box sx={{ width: 300 }}>
      <Autocomplete options={regions} value={selectedOption} renderInput={renderRegionsInput} size="small" onChange={handleRegionChange} />
    </Box>
  );
};

export default Filter;
